/**
 * Central Unavailable Overlay
 *
 * SUITE STANDARD v2.3.0:
 * - Full-screen, non-dismissible overlay
 * - Shown when Central is unreachable, times out, or returns malformed response
 * - Retry button re-calls switch-context
 * - No fallback to cached data, no read-only bypass
 * - Fail-closed: protected UI remains blocked
 */

import { useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface CentralUnavailableOverlayProps {
  errorMessage: string;
  onRetry: () => Promise<void> | void;
}

export function CentralUnavailableOverlay({
  errorMessage,
  onRetry,
}: CentralUnavailableOverlayProps) {
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await onRetry();
    } finally {
      // If retry succeeds, BootGate unmounts this component.
      // If it fails, BootGate re-renders this component with new error.
      setIsRetrying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900 bg-opacity-90">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-100 flex items-center justify-center">
          <AlertTriangle className="h-8 w-8 text-yellow-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Central Unavailable
        </h2>
        <p className="text-gray-600 mb-2">
          Unable to connect to the LevelUp Suite platform.
        </p>
        <p className="text-xs text-gray-400 font-mono mb-6 break-all">
          {errorMessage}
        </p>

        <button
          onClick={handleRetry}
          disabled={isRetrying}
          className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-legacy-600 text-white rounded-lg hover:bg-legacy-700 disabled:opacity-50 disabled:cursor-wait transition-colors font-medium"
        >
          {isRetrying ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin" />
              Retrying...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4" />
              Retry
            </>
          )}
        </button>

        <p className="mt-4 text-xs text-gray-400">
          This overlay cannot be dismissed. The application requires Central to
          function.
        </p>
      </div>
    </div>
  );
}
